import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, Link } from 'react-router-dom';
import './IOSDownloadPage.css';
import { usePostHog } from 'posthog-js/react';
import { auth, db, functions } from './firebase';
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

function IOSDownloadPage() {
    const { t } = useTranslation();
    const { requestId } = useParams();
    const location = useLocation();
    const [imageUrl, setImageUrl] = useState('');
    const [stravaId, setStravaId] = useState('');
    const posthog = usePostHog();

    // User authentication state
    const [currentUser, setCurrentUser] = useState(null);
    const [requestData, setRequestData] = useState(null);

    // Watermark check state
    const [isCheckingWatermark, setIsCheckingWatermark] = useState(false);
    const [hasExistingWatermark, setHasExistingWatermark] = useState(false);
    const [showWatermarkButton, setShowWatermarkButton] = useState(false);
    const [watermarkCheckStatus, setWatermarkCheckStatus] = useState(null);
    const [watermarkCheckError, setWatermarkCheckError] = useState(null);

    // Dialog state
    const [showDialog, setShowDialog] = useState(false);
    const [dialogContent, setDialogContent] = useState({
        type: 'success', // 'success', 'error', 'info'
        title: '',
        message: '',
        icon: ''
    });

    // Helper function to show custom dialog instead of alert
    const showCustomDialog = (type, title, message, icon = '') => {
        setDialogContent({ type, title, message, icon });
        setShowDialog(true);
    };

    // Helper function to check if map was generated before watermark implementation
    const isLegacyMap = () => {
        if (!requestData) return false;

        // Watermark feature was implemented on June 26, 2025
        const watermarkImplementationDate = new Date('2025-06-26T00:00:00Z');

        // Check various possible date fields
        const generationDate =
            requestData.createdAt?.toDate?.() ||
            requestData.timestamp?.toDate?.() ||
            requestData.generatedAt?.toDate?.() ||
            requestData.generationStartTime?.toDate?.() ||
            (requestData.createdAt instanceof Date ? requestData.createdAt : null) ||
            (requestData.timestamp instanceof Date ? requestData.timestamp : null) ||
            (requestData.generatedAt instanceof Date ? requestData.generatedAt : null) ||
            (requestData.generationStartTime instanceof Date ? requestData.generationStartTime : null);

        if (!generationDate) return false;

        return generationDate < watermarkImplementationDate;
    };

    // Function to check for existing watermark in the request
    const checkExistingWatermark = async () => {
        if (!requestId || !currentUser) return;

        try {
            // Check if watermark result exists in private subcollection
            const watermarkResultDoc = await getDoc(
                doc(db, 'GenerationRequests', requestId, 'private', 'watermarkResult')
            );

            if (watermarkResultDoc.exists()) {
                const checkData = watermarkResultDoc.data();
                setHasExistingWatermark(checkData.summary?.hasWatermark || false);
            }
        } catch (error) {
            console.error("Error checking existing watermark:", error);
        }
    };

    // Function to run watermark check
    const runWatermarkCheck = async () => {
        if (!requestId || !requestData?.StravaId || !currentUser) {
            showCustomDialog(
                'info',
                t('ios.authRequiredTitle'),
                t('ios.authRequiredMessage'),
                'ti-info-circle'
            );
            return;
        }

        setIsCheckingWatermark(true);

        try {
            const checkWatermarksFunc = httpsCallable(functions, 'checkActivityWatermarks');
            const result = await checkWatermarksFunc({ requestId: requestId });

            setHasExistingWatermark(result.data.summary?.hasWatermark || false);

            if (result.data.summary?.hasWatermark) {
                showCustomDialog(
                    'success',
                    t('ios.foundTitle'),
                    t('ios.foundMessage'),
                    'ti-check-circle'
                );
            } else {
                showCustomDialog(
                    'info',
                    t('ios.notFoundTitle'),
                    <>
                        {t('ios.notFoundMessage')}{' '}
                        <a href="https://discord.gg/fprRUxfyu4" target="_blank" rel="noopener noreferrer" style={{ color: '#5865F2', textDecoration: 'underline' }}>
                            Discord
                        </a>
                    </>,
                    'ti-search'
                );
            }

            posthog?.capture('watermark_check_run', {
                request_id: requestId,
                activity_id: requestData.StravaId,
                watermark_found: result.data.summary?.hasWatermark || false,
                highest_confidence: result.data.summary?.highestWatermarkConfidence || 0
            });
        } catch (error) {
            console.error("Error running watermark check:", error);
            showCustomDialog(
                'error',
                t('ios.checkFailedTitle'),
                <>
                    {t('ios.checkFailedMessage')}{' '}
                    <a href="https://discord.gg/fprRUxfyu4" target="_blank" rel="noopener noreferrer" style={{ color: '#5865F2', textDecoration: 'underline' }}>
                        Discord
                    </a>
                </>,
                'ti-alert-circle'
            );
        } finally {
            setIsCheckingWatermark(false);
        }
    };

    useEffect(() => {
        // Get parameters from URL query string
        const searchParams = new URLSearchParams(location.search);
        const imageUrlParam = searchParams.get('imageUrl');
        const stravaIdParam = searchParams.get('stravaId');

        if (imageUrlParam) {
            // URLSearchParams.get() already decodes the parameter
            // Use the URL directly (it could be a Firebase Storage URL or regular URL)
            setImageUrl(imageUrlParam);
        }

        if (stravaIdParam) {
            setStravaId(stravaIdParam);
        }
    }, [location.search, requestId]);

    useEffect(() => {
        // Track iOS download page visits
        posthog?.capture('ios_download_page_viewed', {
            request_id: requestId,
            has_strava_id: !!stravaId,
            has_image_url: !!imageUrl
        });
    }, [requestId, stravaId, imageUrl, posthog]);

    // Add auth state listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Fetch request data
    useEffect(() => {
        const fetchRequestData = async () => {
            if (!requestId) return;

            try {
                const docRef = doc(db, "GenerationRequests", requestId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setRequestData(data);

                    // Update watermark status
                    setWatermarkCheckStatus(data.watermarkCheckStatus || null);
                    setWatermarkCheckError(data.watermarkCheckError || null);
                    setHasExistingWatermark(data.hasWatermark || false);

                    // Show watermark button if map is complete and has Strava ID
                    if (data.status === 'openai_processing_complete' && data.StravaId) {
                        setShowWatermarkButton(true);
                    }
                }
            } catch (error) {
                console.error("Error fetching request data:", error);
            }
        };

        fetchRequestData();
    }, [requestId]);

    // Check for existing watermark when we have both request data and user
    useEffect(() => {
        if (requestId && requestData?.StravaId && currentUser) {
            checkExistingWatermark();
        }
    }, [requestId, requestData?.StravaId, currentUser]);

    // Add to your Strava button click
    const handleStravaButtonClick = () => {
        posthog?.capture('ios_strava_activity_link_clicked', {
            strava_id: stravaId,
            request_id: requestId
        });
    };

    if (!imageUrl) {
        return (
            <div className="ios-download-container">
                <div className="ios-download-card">
                    <h1>{t('ios.errorTitle')}</h1>
                    <p>{t('ios.imageMissing')}</p>
                    {requestId ? (
                        <Link to={`/map/${requestId}`} className="ios-back-button">
                            &larr; {t('common.goBack')}
                        </Link>
                    ) : (
                        <Link to="/history" className="ios-back-button">
                            &larr; {t('ios.backToHistory')}
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="ios-download-container">
            <div className="ios-download-card">
                <h1>{t('ios.saveYourMap')}</h1>

                <div className="ios-image-container">
                    <img
                        src={imageUrl}
                        alt={t('ios.imageAlt')}
                        className="ios-ai-image"
                    />
                    <div className="ios-touch-hint">
                        <div className="pulse-circle"></div>
                        <div className="finger-icon">👆</div>
                    </div>
                </div>

                <div className="ios-instructions">
                    <div className="instruction-step">
                        <div className="step-number">1</div>
                        <p>{t('ios.step1')}</p>
                    </div>

                    <div className="instruction-step">
                        <div className="step-number">2</div>
                        <p>{t('ios.step2')}</p>
                    </div>

                    {stravaId && (
                        <>
                            <div className="instruction-step">
                                <div className="step-number">3</div>
                                <p>{t('ios.step3')}</p>
                            </div>

                            <a
                                href={`https://www.strava.com/activities/${stravaId}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="strava-button"
                                onClick={handleStravaButtonClick}
                            >
                                {t('ios.goToStrava')}
                            </a>

                            <div className="strava-tip">
                                <div className="tip-icon">💡</div>
                                <p>{t('ios.stravaTip')}</p>
                            </div>
                        </>
                    )}
                </div>

                {/* Share Credit Check Section */}
                {showWatermarkButton && currentUser && requestData?.StravaId && requestData?.userId === currentUser.uid && (
                    <div className="watermark-check-section">
                        {isLegacyMap() ? (
                            <div className="legacy-map-message">
                                <i className="ti ti-info-circle" style={{ fontSize: '20px', color: '#2563EB' }}></i>
                            <div className="legacy-map-content">
                                <h4>{t('ios.legacyTitle')}</h4>
                                <p>
                                    {t('ios.legacyDesc')}
                                </p>
                                    {requestData?.StravaId && (
                                        <Link
                                            to={`/generate/strava/${requestData.StravaId}`}
                                            className="generate-new-map-button"
                                            onClick={() => posthog?.capture('legacy_map_regenerate_clicked', {
                                                request_id: requestId,
                                                strava_id: requestData.StravaId
                                            })}
                                        >
                                            <i className="ti ti-plus" style={{ fontSize: '16px' }}></i>
                                            {t('ios.generateNew')}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        ) : hasExistingWatermark ? (
                            <div className="watermark-credit-applied">
                                <i className="ti ti-circle-check" style={{ fontSize: '20px', color: '#10b981' }}></i>
                                <span>{t('ios.creditApplied')}</span>
                            </div>
                        ) : watermarkCheckStatus === 'checking' ? (
                            <div className="watermark-check-in-progress">
                                <div className="loading-spinner-small"></div>
                                <span>{t('ios.checkingActivity')}</span>
                            </div>
                        ) : watermarkCheckStatus === 'failed' ? (
                            <div className="watermark-check-error">
                                <i className="ti ti-alert-circle" style={{ fontSize: '20px', color: '#dc2626' }}></i>
                                <span>{watermarkCheckError || t('ios.checkFailedGeneric')}</span>
                                <button
                                    onClick={runWatermarkCheck}
                                    className="watermark-check-button retry"
                                    style={{ marginTop: '10px' }}
                                >
                                    <i className="ti ti-refresh" style={{ fontSize: '16px' }}></i>
                                    {t('ios.tryAgain')}
                                </button>
                            </div>
                        ) : (
                            <div className="watermark-check-prompt">
                                <p>{t('ios.sharedPrompt')}</p>
                                <button
                                    onClick={runWatermarkCheck}
                                    disabled={isCheckingWatermark || watermarkCheckStatus === 'checking'}
                                    className="watermark-check-button"
                                >
                                    {isCheckingWatermark || watermarkCheckStatus === 'checking' ? (
                                        <>
                                            <div className="loading-spinner-small"></div>
                                            {t('ios.checking')}
                                        </>
                                    ) : (
                                        <>
                                            <i className="ti ti-photo-search" style={{ fontSize: '18px' }}></i>
                                            {t('ios.claimCredit')}
                                        </>
                                    )}
                                </button>
                                <small>{t('ios.earnPerMap')}</small>
                            </div>
                        )}
                    </div>
                )}

                {requestId ? (
                    <Link to={`/map/${requestId}`} className="ios-back-button">
                        &larr; {t('ios.backToComparison')}
                    </Link>
                ) : (
                    <Link to="/history" className="ios-back-button">
                        &larr; {t('ios.backToHistory')}
                    </Link>
                )}
            </div>

            {/* Custom Dialog */}
            {showDialog && (
                <div className="dialog-overlay" onClick={() => setShowDialog(false)}>
                    <div className="dialog-container" onClick={(e) => e.stopPropagation()}>
                        <div className={`dialog-content ${dialogContent.type}`}>
                            <div className="dialog-header">
                                {dialogContent.icon && (
                                    <i className={`${dialogContent.icon} dialog-icon`}></i>
                                )}
                                <h3 className="dialog-title">{dialogContent.title}</h3>
                            </div>
                            <div className="dialog-body">
                                <div className="dialog-message">{dialogContent.message}</div>
                            </div>
                            <div className="dialog-actions">
                                <button
                                    onClick={() => setShowDialog(false)}
                                    className="dialog-button primary"
                                >
                                    {t('common.ok', 'OK')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default IOSDownloadPage; 